import React, { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import gfm from 'remark-gfm'
import { ChevronDown, ListTree } from 'lucide-react'
import organizeReport from './reportMarkdownStructure'
import './markdown.less'

function ReportDocument({ node, children }) {
    const rootRef = useRef(null)
    const chapters = node.children.filter((child) => child.type === 'reportSection')
    const [active, setActive] = useState(chapters[0] && chapters[0].id)

    useEffect(() => {
        const root = rootRef.current
        const headings = Array.from(root.querySelectorAll('.inventory-prose-section'))
        if (!headings.length) return
        let frame
        const update = () => {
            cancelAnimationFrame(frame)
            frame = requestAnimationFrame(() => {
                // 捕获外层容器的滚动，兼容页面嵌入云创工作区的滚动区域。
                const toc = root.querySelector('.inventory-document-toc').getBoundingClientRect()
                const top = window.matchMedia('(max-width: 800px)').matches ? toc.bottom + 24 : toc.top + 36
                const passed = headings.filter((element) => element.getBoundingClientRect().top <= top)
                const current = passed[passed.length - 1] || headings[0]
                if (current) setActive(current.id)
            })
        }
        window.addEventListener('scroll', update, true)
        window.addEventListener('resize', update)
        root.addEventListener('toggle', update, true)
        update()
        return () => {
            cancelAnimationFrame(frame)
            window.removeEventListener('scroll', update, true)
            window.removeEventListener('resize', update)
            root.removeEventListener('toggle', update, true)
        }
    }, [node])

    const goTo = (id) => {
        const target = rootRef.current.querySelector(`#${id}`)
        if (!target) return
        setActive(id)
        target.focus({ preventScroll: true })
        // 云创使用 hash 路由，章节定位不修改 URL，避免触发页面路由切换。
        target.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })
    }
    return (
        <div className={`inventory-document${chapters.length ? '' : ' inventory-document--plain'}`} ref={rootRef}>
            {chapters.length > 0 && (
                <aside className="inventory-document-toc">
                    <div className="inventory-document-toc-label">
                        <ListTree size={15} />
                        章节导航
                    </div>
                    <nav aria-label="报告章节导航">
                        {chapters.map((chapter) => (
                            <button key={chapter.id} aria-current={active === chapter.id ? 'location' : undefined} onClick={() => goTo(chapter.id)}>
                                <span>{String(chapter.number).padStart(2, '0')}</span>
                                {chapter.title}
                            </button>
                        ))}
                    </nav>
                </aside>
            )}
            <article className="inventory-markdown">{children}</article>
        </div>
    )
}

function Section({ id, number, kind, empty, children }) {
    const [heading, ...content] = React.Children.toArray(children)
    return (
        <section id={id} tabIndex={-1} className={`inventory-prose-section inventory-prose-section--${kind}`}>
            <header className="inventory-prose-heading">
                <span>{String(number).padStart(2, '0')}</span>
                {heading}
                {empty && <small>本节未提供正文</small>}
            </header>
            {!empty && <div className="inventory-prose-section-body">{content}</div>}
        </section>
    )
}

function Block({ kind, children }) {
    return <div className={`inventory-prose-block inventory-prose-block--${kind}`}>{children}</div>
}

function Evidence({ count, children }) {
    const [heading, ...content] = React.Children.toArray(children)
    return (
        <details className="inventory-prose-evidence">
            <summary>
                {heading}
                <span>{count} 项明细</span>
                <ChevronDown size={16} />
            </summary>
            <div className="inventory-prose-evidence-body">{content}</div>
        </details>
    )
}

function Field({ label, field, children }) {
    return (
        <li className={`inventory-prose-field${field === '建议' ? ' inventory-prose-field--recommendation' : ''}`}>
            <span className="inventory-prose-field-label">{label}</span>
            <div>{children}</div>
        </li>
    )
}

function Heading({ level, children }) {
    return React.createElement(`h${level}`, null, children)
}

const renderers = {
    root: ReportDocument,
    reportSection: Section,
    reportBlock: Block,
    reportEvidence: Evidence,
    reportField: Field,
    heading: Heading,
    image: ({ alt }) => (alt ? <span>{alt}</span> : null),
    imageReference: ({ alt }) => (alt ? <span>{alt}</span> : null),
}
const transforms = [organizeReport]
const plugins = [gfm]

export default function ReportMarkdown({ markdown }) {
    return <ReactMarkdown source={markdown} escapeHtml plugins={plugins} astPlugins={transforms} renderers={renderers} />
}
